import * as React from 'react';

import styles from './Loading.module.css';

/** Visual size of the loading indicator, matching Figma "Size" property. */
type LoadingSize = 'xs' | 'sm' | 'md' | 'lg';

export interface LoadingProps extends React.ComponentPropsWithRef<'div'> {
  /** Visual size of the loading indicator. @default 'md' */
  size?: LoadingSize;
  /**
   * Whether to show a white rounded background behind the icon.
   * Matches Figma "Icon Background Color" property. @default false
   */
  iconBackground?: boolean;
  /**
   * Optional text label displayed below the icon box.
   * If omitted, no label is rendered; the component still announces
   * "Loading" to screen readers via a visually-hidden element.
   */
  label?: string;
}

// ─── Internal icon ────────────────────────────────────────────────────────────

/** ViewSonic dots-slant icon with staggered bounce animation. Exported for use in Button. */
export function DotsSlantIcon() {
  return <div aria-hidden="true" className={styles.icon} />;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Loading — an animated indicator that communicates a pending or in-progress state.
 *
 * Renders the ViewSonic dots-slant icon (three colored dots) spinning continuously.
 * An optional rounded-rectangle background can be shown behind the icon, and an
 * optional text label can be displayed below.
 *
 * The component announces its state to screen readers via `role="status"`.
 * When no visible `label` is provided, a visually-hidden "Loading" element ensures
 * assistive technology users receive feedback.
 *
 * @example
 * // Default (medium size, no background, no label)
 * <Loading />
 *
 * @example
 * // Large with icon background and label
 * <Loading size="lg" iconBackground label="資料載入中" />
 *
 * @example
 * // Small with background, no label
 * <Loading size="sm" iconBackground />
 */
const Loading = React.forwardRef<HTMLDivElement, LoadingProps>(
  (
    { size = 'md', iconBackground = false, label, className, ...props },
    ref
  ) => {
    const rootClass = [styles.root, styles[`size-${size}`], className]
      .filter(Boolean)
      .join(' ');

    const iconBoxClass = [
      styles.iconBox,
      styles[`iconBox-${size}`],
      iconBackground ? styles.iconBoxBackground : '',
    ]
      .filter(Boolean)
      .join(' ');

    const iconWrapClass = [styles.iconWrap, styles[`iconWrap-${size}`]].join(
      ' '
    );

    const labelClass = [styles.label, styles[`label-${size}`]].join(' ');

    return (
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        data-vsds-motion-role="essential"
        className={rootClass}
        {...props}
      >
        {/* Icon box — always rendered; background is conditional */}
        <div className={iconBoxClass}>
          <span className={iconWrapClass}>
            <DotsSlantIcon />
          </span>
        </div>

        {/* Label or sr-only fallback for assistive technology */}
        {label !== undefined ? (
          <span className={labelClass}>{label}</span>
        ) : (
          <span className={styles.srOnly}>Loading</span>
        )}
      </div>
    );
  }
);

Loading.displayName = 'Loading';

export { Loading };
